import React from "react";
import { useFormik } from "formik";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import {
  Input,
  Label,
  CheckBoxWithLabel,
} from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import {
  ButtonPrimary,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../../components/utils";
import { FileDropzone } from "../../../components/shared/FileDropzone";
import { DocumentType } from "../../../types/DocumentsInterface";
import { useGetEmployeePortalSettings } from "../../../queries/siteSettings";
import { Instructions } from "../../../components/shared/Instructions";

interface PlanDocumentCreateProps {
  uploadFormik: any; // using any for formik instance convenience, or can type strictly
  DOCUMENT_TYPES: { label: string; value: DocumentType }[];
  isLoading: boolean;
  onCancel: () => void;
  isEditMode?: boolean;
  existingFileName?: string;
}

export const PlanDocumentCreate: React.FC<PlanDocumentCreateProps> = ({
  uploadFormik,
  DOCUMENT_TYPES,
  isLoading,
  onCancel,
  isEditMode = false,
  existingFileName,
}) => {
  const { data: employeePortalSettings } = useGetEmployeePortalSettings();
  return (
    <VStack className="w-full px-10 gap-9 py-7 h-full overflow-y-auto">
      <form onSubmit={uploadFormik.handleSubmit} className="contents">
        <HStack className="items-start gap-8">
          <div className="flex-1">
            <Label className="required">Document Type</Label>
            <Select
              options={DOCUMENT_TYPES}
              textGetter={(v: any) => v.label}
              valueGetter={(v: any) => v.value}
              onChange={(e: any) =>
                uploadFormik.setFieldValue(
                  "documentType",
                  e.target ? e.target.value : e
                )
              }
              placeholder="Select Document Type"
              className="mt-1"
              value={uploadFormik.values.documentType}
            />
            {uploadFormik.touched.documentType &&
              uploadFormik.errors.documentType && (
                <Error text={uploadFormik.errors.documentType as string} />
              )}
          </div>
          <div className="flex-1">
            <Label className="required">Document Name</Label>
            <Input
              placeholder="e.g. 2024 Stock Plan Amendment"
              {...uploadFormik.getFieldProps("name")}
            />
            {uploadFormik.touched.name && uploadFormik.errors.name && (
              <Error text={uploadFormik.errors.name as string} />
            )}
          </div>
        </HStack>

        <div className="w-full">
          <Label className={isEditMode ? "" : "required"}>File</Label>
          <div className="mt-1">
            <FileDropzone
              instructionText={
                isEditMode
                  ? "File updates are not allowed in edit mode."
                  : "Upload PDF files only (.pdf)"
              }
              acceptedFileExtensions={[".pdf"]}
              onFileSelect={(file) => {
                if (file && file.size > 2 * 1024 * 1024) {
                  toast.error("File size exceeds the 2 MB limit.");
                  return;
                }
                uploadFormik.setFieldValue("file", file);
                if (!uploadFormik.values.name) {
                  uploadFormik.setFieldValue("name", file.name.split(".")[0]);
                }
              }}
              onClear={() => uploadFormik.setFieldValue("file", null)}
              selectedFile={
                isEditMode && existingFileName
                  ? ({ name: existingFileName } as File)
                  : uploadFormik.values.file || undefined
              }
              disabled={isEditMode}
            />
            {uploadFormik.touched.file && uploadFormik.errors.file && (
              <Error text={uploadFormik.errors.file as string} />
            )}
          </div>
        </div>

        <HStack className="items-center gap-8">
          <div className="flex-1">
            <Label>Tags</Label>
            <CreatableSelect
              isMulti
              options={[]}
              value={uploadFormik.values.tags}
              onChange={(selected: any) =>
                uploadFormik.setFieldValue("tags", selected || [])
              }
              placeholder="Type and press enter to add tags"
            />
          </div>
          <div className="flex-1"></div>
        </HStack>

        <HStack className="items-center gap-8">
          <CheckBoxWithLabel
            label="Show document to Employee"
            labelclassname="ml-2"
            name="shownToEmployee"
            checked={uploadFormik.values.shownToEmployee}
            onChange={(e: any) =>
              uploadFormik.setFieldValue("shownToEmployee", e.target.checked)
            }
            className="text-white accent-orange-501 w-4 h-4"
          />
          {uploadFormik.values.shownToEmployee && (
            <CheckBoxWithLabel
              label="Allow Employee to Download"
              labelclassname="ml-2"
              name="canEmployeeDownload"
              checked={uploadFormik.values.canEmployeeDownload}
              onChange={(e: any) =>
                uploadFormik.setFieldValue(
                  "canEmployeeDownload",
                  e.target.checked
                )
              }
              className="text-white accent-orange-501 w-4 h-4"
            />
          )}
        </HStack>

        {(() => {
          const messages = [
            ...(employeePortalSettings?.showPlanToEmployees !== true &&
            uploadFormik.values.shownToEmployee
              ? [
                  "Plan documents will not be visible to employees. Enable 'Show Plan to Employees' in Employee Portal Settings.",
                ]
              : []),
            ...(uploadFormik.values.shownToEmployee &&
            employeePortalSettings?.allowEmployeesToDownloadPlanDocuments !==
              true &&
            uploadFormik.values.canEmployeeDownload
              ? [
                  "Employee downloads are disabled in settings. Enable 'Allow Employees to Download Plan Documents' in Employee Portal Settings to allow downloads.",
                ]
              : []),
          ];

          return messages.length > 0 ? (
            <Instructions data={messages} seperator="," />
          ) : null;
        })()}

        <HStack className="justify-between pt-4">
          <ButtonSecondary type="button" onClick={onCancel}>
            Cancel
          </ButtonSecondary>
          <ButtonPrimary
            type="submit"
            disabled={isLoading || !uploadFormik.dirty}
            // @ts-ignore
            loading={isLoading}
          >
            Save
          </ButtonPrimary>
        </HStack>
      </form>
    </VStack>
  );
};
